import { inject, Injectable } from '@angular/core';
import { FilterBy } from '@shared/sync/filter-by.model';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { Contact } from '../models/contact.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private readonly _serviceKey = 'contact';
  private readonly _blazorService = inject(HsBlazorService);
  public readonly _limit = 2000;

  public async getLetters(
    alphabet: string[],
    orderBy: string | null = null,
  ): Promise<{ letter: string; isRead: boolean }[]> {
    const letters = await this._blazorService.invokeService(
      this._serviceKey,
      'GetLetters_V2',
      { alphabet, orderBy },
    );

    if (letters) {
      return (letters as Record<string, { letter: string; isRead: boolean }[]>)[
        'letters'
      ];
    }

    return [];
  }

  public async getAll(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
  ): Promise<IPaginatedItemsModel<Contact>> {
    const contactsResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetAll_V2',
      { pageIndex, pageSize, sortBy, filterBy },
    );

    if (contactsResult) {
      return (contactsResult as Record<string, IPaginatedItemsModel<Contact>>)[
        'result'
      ];
    }

    return { pageIndex: 0, pageSize: 0, count: 0, data: [] };
  }

  public async getContactById(contactId: string): Promise<Contact | undefined> {
    const contact = await this._blazorService.invokeService(
      this._serviceKey,
      'GetById_V2',
      { id: contactId },
    );

    if (contact) {
      return (contact as Record<string, Contact | undefined>)['contact'];
    }

    return undefined;
  }

  public async getContactsByLetter(
    letter: string,
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
  ): Promise<IPaginatedItemsModel<Contact>> {
    const contactsResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Get_V2',
      { letter, pageIndex, pageSize, sortBy },
    );

    if (contactsResult) {
      return (contactsResult as Record<string, IPaginatedItemsModel<Contact>>)[
        'result'
      ];
    }

    return { pageIndex: 0, pageSize: 0, count: 0, data: [] };
  }

  public async getTenantContact(): Promise<Contact | undefined> {
    const contactsResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetMandant',
    );

    return contactsResult as Contact | undefined;
  }

  public async create(
    contactDto: Contact,
    pageSize: number,
  ): Promise<Contact | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Create_V2',
      {
        contact: contactDto,
        pageSize: pageSize,
        limit: this._limit,
      },
    );

    if (result)
      return (result as Record<string, Contact | undefined>)['contact'];

    return undefined;
  }

  public async updateTenantContact(
    contact: Contact,
  ): Promise<Contact | undefined> {
    const contactsResult = await this._blazorService.invokeService(
      this._serviceKey,
      'UpdateTenant',
      contact,
    );

    return contactsResult as Contact | undefined;
  }

  public async update(contact: Contact): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Update_V2',
      { contact: contact },
    );

    return (
      !!result &&
      (result as Record<string, Contact | undefined>)['contact'] !== undefined
    );
  }

  public async delete(contact: Contact): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'SoftDelete',
      contact,
    );

    return result === true;
  }

  /**
   * Returns true if any contact OTHER than excludeContactUid already has a
   * CustomerReference registration with the given registrationNumber.
   */
  public async isCustomerNumberDuplicate(
    registrationNumber: string,
    excludeContactUid: string,
  ): Promise<boolean> {
    if (!registrationNumber.trim()) return false;

    const result = await this.getAll(this._limit);
    return result.data.some(
      (c) =>
        c.uId !== excludeContactUid &&
        c.registrations?.some(
          (r) =>
            r.registrationType === 5 /* CustomerReference */ &&
            r.registrationNumber?.trim() === registrationNumber.trim(),
        ),
    );
  }
}
